import { z } from "zod";

import { CurrencyType, KidsPolicyType, MealType } from "@/lib/gql/__generated__/graphql";
import {
	DayOfTheWeekSchema,
	KidsPolicyItemInputSchema,
	MealTypeSchema,
} from "@/lib/zod/__generated__/validation-schema";
import { requiredValue } from "@/lib/zod/custom-validation-schema";

// 空文字列を許可しない文字列
export const nonEmptyStringZodSchema = z.string().refine((value) => value.length > 0, { message: "必須項目です" });

// 画像
const s3Object = z.object({
	bucket: z.string().refine((value) => !!value, { message: "必須項目です" }),
	fileName: z.string().optional().nullable(),
	key: z.string().refine((value) => !!value, { message: "必須項目です" }),
	mimeType: z.string().optional().nullable(),
	region: z.string().refine((value) => !!value, { message: "必須項目です" }),
});

const imageS3Object = z.object({
	url: z.string(),
	s3Object,
});

export const mainKeyVisualZodSchema = imageS3Object;
export const s3ObjectZodSchema = s3Object;

export const keyVisualsZodSchema = z.array(imageS3Object);

// 予約締め切り
export const requiredPrepMilliSecondBeforeDateZodSchema = z.union([z.coerce.number(), z.coerce.number()]);

export const requiredPrepMilliSecondBeforeHourZodSchema = z.union([z.coerce.number(), z.coerce.number()]);

// 施設に関する注意事項（FAQ のようなもの）
export const hotelInfoZodSchema = z.array(
	z.object({
		title: z.string().min(1, "タイトルには1文字以上入力してください").max(300),
		description: z.string().min(1, "説明には1文字以上入力してください").max(3_000),
	}),
);

/**
 * 料金に関するzodのバリデーションスキーマ
 *
 * - 10,000,000円以下
 * - 必須
 * - 整数
 */
export const priceObjectZodSchema = z.object({
	amount: z
		.number()
		.int()
		.max(10_000_000, { message: "金額には10,000,000円以下の値を入力してください" })
		.refine(requiredValue<number>, { message: "必須項目です" }),
	currencyType: z.nativeEnum(CurrencyType),
});

/**
 * 料金に関するzodのバリデーションスキーマ
 *
 * - 任意円以下
 * - 必須
 * - 整数
 */
export const priceObjectZodSchemaFunc = (maxAmount: number = 10_000_000) =>
	z.object({
		amount: z
			.number()
			.int()
			.max(maxAmount, { message: `金額には${maxAmount.toLocaleString()}円以下の値を入力してください` })
			.refine(requiredValue<number>, { message: "必須項目です" }),
		currencyType: z.nativeEnum(CurrencyType),
	});

export type PriceObjectZodSchemaType = z.infer<typeof priceObjectZodSchema>;

/**
 * 非負の料金に関するzodのバリデーションスキーマ
 *
 * - 0円以上
 * - 10,000,000円以下
 * - 必須
 * - 整数
 */
export const nonNegativePriceObjectZodSchema = z.object({
	amount: z
		.number()
		.int()
		.min(0, { message: "0円以上の値を入力してください" })
		.max(10_000_000, { message: "金額には10,000,000円以下の値を入力してください" })
		.refine(requiredValue<number>, { message: "必須項目です" }),
	currencyType: z.nativeEnum(CurrencyType),
});

const SLUG_REGEX = /^[a-zA-Z0-9-_]+$/;
/**
 * slug に関するzodのバリデーションスキーマ
 */
export const slugZodSchema = z
	.string()
	.max(100)
	.regex(SLUG_REGEX, { message: "半角英数字、ハイフン（-）、アンダースコア（_）のみ使用できます" });

// 半角小文字英数字とハイフンのみ使用できる（大文字不可）
const DOMAIN_REGEX = /^[a-z0-9-]+$/;
/**
 * ドメイン名に関するzodのバリデーションスキーマ
 */
export const domainNameZodSchema = z
	.string()
	.max(100)
	.regex(DOMAIN_REGEX, { message: "半角小文字（大文字不可）、英数字、ハイフン（-）のみ使用できます" });

/**
 * 郵便番号の形式
 * - 3桁-4桁
 * - 7桁
 * - 全て半角数字
 */
const POSTAL_CODE_REGEX = /^[0-9]{3}-[0-9]{4}|[0-9]{7}$/;
/**
 * 郵便番号に関するzodのバリデーションスキーマ
 */
export const postalCodeZodSchema = z.string().refine(
	(value) => {
		if (value.length === 0) {
			return true;
		}

		return POSTAL_CODE_REGEX.test(value);
	},
	{
		message: "郵便番号は「3桁-4桁」または「7桁」で入力してください（全て半角数字）",
	},
);

/**
 * 緯度・経度 に関するzodのバリデーションスキーマ
 *
 * - 緯度と経度の両方を設定する必要がある（両方設定しないはOK）
 * - 緯度は -90 から 90 まで
 * - 経度は -180 から 180 まで
 */
export const geoLocationInputSchema = z
	.object({
		lat: z.coerce.number({ invalid_type_error: "数値を入力してください" }).nullish(),
		lon: z.coerce.number({ invalid_type_error: "数値を入力してください" }).nullish(),
	})
	.nullish()
	.superRefine((args, ctx) => {
		if (!args) {
			return z.NEVER;
		}

		const { lat, lon } = args;

		if (!!lat && !lon) {
			ctx.addIssue({
				path: ["lon"],
				code: z.ZodIssueCode.custom,
				message: "経度を設定してください",
			});
		} else if (!!lon && !lat) {
			ctx.addIssue({
				path: ["lat"],
				code: z.ZodIssueCode.custom,
				message: "緯度を設定してください",
			});
		} else if (!!lat && (lat < -90 || 90 < lat)) {
			ctx.addIssue({
				path: ["lat"],
				code: z.ZodIssueCode.custom,
				message: "緯度には -90 から 90 までの値を入力してください",
			});
		} else if (!!lon && (lon < -180 || 180 < lon)) {
			ctx.addIssue({
				path: ["lon"],
				code: z.ZodIssueCode.custom,
				message: "経度には -180 から 180 までの値を入力してください",
			});
		}

		return z.NEVER;
	});

/**
 * GTM ID の最大文字数
 * - GTM- + 8文字の文字
 *
 * GTM ID は 11桁 または 12桁
 */
export const GTMID_ZOD_SCHEMA_MAX_LENGTH = 4 + 8;

/**
 * GTM ID に関するzodのバリデーションスキーマ
 */
export const gtmIDZodSchema = z
	.string()
	.nullish()
	.optional()
	.superRefine((value, ctx) => {
		if (!value) {
			return z.NEVER;
		}

		if (!value.startsWith("GTM-")) {
			return ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "GTM-から始まる必要があります",
			});
		}

		if (GTMID_ZOD_SCHEMA_MAX_LENGTH < value.length) {
			return ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: `11文字、または12文字で入力してください`,
			});
		}

		return z.NEVER;
	});

/**
 * 曜日に関するzodのバリデーションスキーマ
 *
 * 現在は「宿泊可能な曜日」のように、複数選択可能な曜日を扱うために使用している
 */
export const dayOfTheWeekZodSchema = z.array(DayOfTheWeekSchema).refine(
	(dayOfTheWeek) => {
		return dayOfTheWeek.length > 0;
	},
	{
		message: "曜日は１つ以上選択してください",
		path: ["value"],
	},
);

/**
 * カード名義に関するzodのバリデーションスキーマ
 *
 * payjp の仕様を元に定義している
 *
 * - 45文字以内
 * - 英数字・スペース・ピリオド・ハイフン（すべて半角のみ）
 */
export const payjpCardHolderNameZodSchema = z
	.string()
	.min(1, { message: "カード名義は1文字以上入力してください" })
	.max(45, { message: "カード名義は45文字以内で入力してください" })
	.regex(/^[a-zA-Z0-9\s.-]+$/, {
		message: "カード名義は英数字・スペース・ピリオド・ハイフン（すべて半角のみ）で入力してください",
	});

// hex カラーコード （6桁か3桁）
const HEX_COLOR_REGEX = /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/;
/**
 * hex カラーコードに関するzodのバリデーションスキーマ
 */
export const hexColorZodSchema = z
	.string()
	.regex(HEX_COLOR_REGEX, {
		message: "6桁か3桁の半角英数字を入力してください",
	})
	.refine((value) => value.startsWith("#"), {
		message: "# から始まる必要があります",
	});

// YYYY-MM-DD の形式であることを検証する
const CHILLNN_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
/**
 * 日付に関するzodのバリデーションスキーマ
 * - YYYY-MM-DD の形式であることを検証する
 */
export const chillnnDateZodSchema = z.string().regex(CHILLNN_DATE_REGEX, {
	message: "日付を選択してください",
});

const SPECIAL_CHARS = "!@#$%^&*()_+-=[]{}\\|;:'\",.<>/?~`";
const VALID_CHARS = `a-zA-Z0-9${SPECIAL_CHARS.replace(/[-\\\]]/g, "\\$&")}`;

const CHILLNN_PASSWORD_REGEX = new RegExp(
	"^" +
		"(?=.*?[a-z])" + // 小文字
		"(?=.*?[A-Z])" + // 大文字
		"(?=.*?\\d)" + // 数字
		`[${VALID_CHARS}]{8,}` +
		"$",
);

/**
 * 最低文字数を受け取り、パスワードのzodのバリデーションスキーマを作る
 * - 文字種の条件（英大文字・英小文字・数字）はログイン用と新しく作るパスワード用で共通
 */
const createChillnnPasswordZodSchema = (minLength: number) =>
	z
		.string()
		.min(minLength, { message: `パスワードは${minLength}文字以上で入力してください` })
		.max(500, { message: "パスワードは500文字以内で入力してください" })
		.regex(CHILLNN_PASSWORD_REGEX, {
			message:
				"<div class='grid grid-cols-2'>" +
				`<div>・${minLength}文字以上</div><div>・数字（0-9）</div>` +
				"<div>・英大文字（A-Z）</div><div>・英小文字（a-z）</div>" +
				"</div>" +
				"<div class='mt-1'>全ての条件を満たすパスワードを入力してください</div>",
		});

/**
 * ログインに関するzodのバリデーションスキーマ
 * - 最低文字数は 8 のまま上げない。上げると、8〜11 文字で登録済みの既存アカウントがログインできなくなる
 */
export const chillnnPasswordZodSchema = createChillnnPasswordZodSchema(8);

/**
 * 新しく作るパスワード（新規登録・招待からの登録・パスワード再設定）に関するzodのバリデーションスキーマ
 * - セキュリティ対策として、ログイン用より長い 12 文字以上を求める。既存アカウントには影響しない
 * - Cognito プールの最短文字数は 8 のまま（プール全体に効くため）。12 文字の条件はクライアント側だけで見ている
 * - 推測されやすさの判定は辞書の読み込みが必要なため、useNewPasswordError で別に行う
 */
export const chillnnNewPasswordZodSchema = createChillnnPasswordZodSchema(12);

export const kidsPolicyItemZodSchema = z.discriminatedUnion("kidsDiscountType", [
	KidsPolicyItemInputSchema.extend({
		kidsDiscountType: z.literal(KidsPolicyType.Static),
		static: nonNegativePriceObjectZodSchema,
	}),
	KidsPolicyItemInputSchema.extend({
		kidsDiscountType: z.literal(KidsPolicyType.NotAvailable),
		static: nonNegativePriceObjectZodSchema.nullish(),
	}),
	KidsPolicyItemInputSchema.extend({
		kidsDiscountType: z.literal(KidsPolicyType.Ratio),
		static: nonNegativePriceObjectZodSchema.nullish(),
	}),
]);
export const kidsPolicyZodSchema = z.array(kidsPolicyItemZodSchema);

export type KidsPolicyItemZodSchemaType = z.infer<typeof kidsPolicyItemZodSchema>;
export type KidsPolicyZodSchemaType = z.infer<typeof kidsPolicyZodSchema>;

/**
 * 残在庫数に関するzodのバリデーションスキーマ
 *
 * - 必須
 * - 整数
 * - 0 以上 1,000 以下
 */
export const stockRemainNumZodSchema = z
	.number({
		required_error: "残在庫の入力は必須です",
		invalid_type_error: "残在庫は数値で入力してください",
	})
	.int()
	.min(0)
	.max(1_000);

/**
 * プランに含まれる食事
 * 素泊まり（食事なし）は食事と同時に選べない。配列に対するエラーなので path は ["value"]（配列ノードの error に反映される）
 */
export const includedMealsZodSchema = z
	.array(MealTypeSchema)
	.refine((includedMeals) => !(includedMeals.includes(MealType.RoomOnly) && includedMeals.length > 1), {
		message: "素泊まりと食事は同時に選べません",
		path: ["value"],
	});
